import React, { useState, useEffect, useRef } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Row,
  Col,
  Label,
  Input,
  Button,
  Spinner,
  Collapse,
} from 'reactstrap';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { updatePurifierOrderDetails } from '../actions/action';
import { getModelList } from '../../ServiceOrders/ManageServiceOrders/actions/action';
import { ErrorToast, SuccessToast } from '../../../utils/Toaster';
import AddAddress from '../../Orders/Cart/components/AddAddress';

const initialForm = {
  master_id: '',
  ls_order_no: '',
  model_no: '',
  sku: '',
  serial_no: '',
  customer_name: '',
  mobile: '',
  alternate_mobile: '',
  email: '',
  building: '',
  flat: '',
  floor: '',
  street: '',
  land_mark: '',
  area: '',
  emirate: '',
  latitude: '',
  longitude: '',
  warranty: '',
  installation_date: '',
  warranty_end_date: '',
  amc_start_date: '',
  amc_end_date: '',
};

const toDateInput = (v) =>
  v && moment(v).isValid() ? moment(v).format('YYYY-MM-DD') : '';

const PurifierEditOffcanvas = ({ isOpen, toggle, order, onSaved }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState([]);
  const [isAddressOpen, setIsAddressOpen] = useState(true);
  const addressFormRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getModelList();
        const raw = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        // Deduplicate by name, keep first sku encountered.
        const seen = new Set();
        const uniq = [];
        raw.forEach((m) => {
          const name = m?.name;
          if (!name || seen.has(name)) return;
          seen.add(name);
          uniq.push({ name, sku: m?.sku || '' });
        });
        if (!cancelled) setModels(uniq);
      } catch (e) {
        console.error('Failed to load purifier models', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !order) return;
    setForm({
      master_id: order.id || '',
      ls_order_no: order.ls_order_no || '',
      model_no: order.model_no || '',
      sku: order.sku || '',
      serial_no: order.serial_no || '',
      customer_name: order.customer_name || '',
      mobile: order.mobile || '',
      alternate_mobile: order.alternate_mobile || '',
      email: order.email || '',
      building: order.building || '',
      flat: order.flat || '',
      floor: order.floor || '',
      street: order.street || '',
      land_mark: order.land_mark || '',
      area: order.area || '',
      emirate: order.emirate || '',
      latitude: order.latitude || '',
      longitude: order.longitude || '',
      warranty: order.warranty || '',
      installation_date: toDateInput(order.installation_date),
      warranty_end_date: toDateInput(order.warranty_end_date),
      amc_start_date: toDateInput(order.amc_start_date),
      amc_end_date: toDateInput(order.amc_end_date),
    });
  }, [isOpen, order]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleModelChange = (e) => {
    const name = e.target.value;
    const matched = models.find((m) => m.name === name);
    setForm((prev) => ({
      ...prev,
      model_no: name,
      sku: matched?.sku || prev.sku,
    }));
  };

  const handleAddressSet = (a) => {
    setForm((prev) => ({
      ...prev,
      building: a.buildingName || prev.building,
      street: a.streetName || prev.street,
      flat: a.flatNo || prev.flat,
      floor: a.floorNumber || prev.floor,
      area: a.areaName || prev.area,
      land_mark: a.nearestLandMark || prev.land_mark,
      latitude: a.latitude || prev.latitude,
      longitude: a.longitude || prev.longitude,
      emirate: a.region || prev.emirate,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await updatePurifierOrderDetails({
        params: form,
        dispatch,
      });
      if (result?.code === 200) {
        SuccessToast(result?.message || 'Purifier order updated.');
        if (onSaved) onSaved();
        toggle();
      } else {
        ErrorToast(result?.message || 'Failed to update.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '65vw', maxWidth: '1100px' }}
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom">
        <span className="fw-bold">Edit Purifier Order</span>
        {order?.ls_order_no && (
          <span className="ms-2 text-muted fw-normal">| {order.ls_order_no}</span>
        )}
      </OffcanvasHeader>
      <OffcanvasBody>
        <form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Label className="form-label">Model No</Label>
              <Input
                type="select"
                name="model_no"
                value={form.model_no}
                onChange={handleModelChange}
              >
                <option value="">Select model</option>
                {form.model_no &&
                  !models.some((m) => m.name === form.model_no) && (
                    <option value={form.model_no}>{form.model_no}</option>
                  )}
                {models.map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6}>
              <Label className="form-label">SKU</Label>
              <Input
                type="text"
                name="sku"
                value={form.sku}
                readOnly
                style={{ backgroundColor: '#e9ecef' }}
              />
            </Col>
            <Col md={6}>
              <Label className="form-label">Serial No</Label>
              <Input
                type="text"
                name="serial_no"
                value={form.serial_no}
                onChange={handleChange}
              />
            </Col>
            <Col md={6}>
              <Label className="form-label">Warranty</Label>
              <Input
                type="text"
                name="warranty"
                value={form.warranty}
                onChange={handleChange}
              />
            </Col>

            <Col md={12}>
              <hr className="my-2" />
              <h6 className="text-muted text-uppercase fw-bold mb-2">Customer</h6>
            </Col>
            <Col md={3}>
              <Label className="form-label">Customer Name</Label>
              <Input
                type="text"
                name="customer_name"
                value={form.customer_name}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">Mobile</Label>
              <Input
                type="text"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">Alternate Mobile</Label>
              <Input
                type="text"
                name="alternate_mobile"
                value={form.alternate_mobile}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">Email</Label>
              <Input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </Col>

            <Col md={12}>
              <hr className="my-2" />
              <div
                className="d-flex justify-content-between align-items-center"
                onClick={() => setIsAddressOpen((p) => !p)}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="text-muted text-uppercase fw-bold mb-0">
                  <i className="bx bx-map-pin me-1"></i>
                  Address Details
                </h6>
                <i
                  className={`bx ${isAddressOpen ? 'bx-chevron-up' : 'bx-chevron-down'}`}
                ></i>
              </div>
            </Col>
            <Col md={12}>
              <Collapse isOpen={isAddressOpen}>
                <div className="border rounded-3 bg-white shadow-sm overflow-hidden mb-3">
                  {order ? (
                    <AddAddress
                      service_order_no={null}
                      showmapandAddress={true}
                      setShowmapandAddress={() => {}}
                      source="serviceOrder"
                      setAddress={handleAddressSet}
                      hideSubmit={true}
                      formRef={addressFormRef}
                      country="ae"
                      initialData={{
                        latitude: form.latitude,
                        longitude: form.longitude,
                        buildingName: form.building,
                        streetName: form.street,
                        flatNo: form.flat,
                        floorNumber: form.floor,
                        areaName: form.area,
                        nearestLandMark: form.land_mark,
                      }}
                    />
                  ) : null}
                </div>
              </Collapse>
            </Col>
            <Col md={12}>
              <hr className="my-2" />
              <h6 className="text-muted text-uppercase fw-bold mb-2">
                Installation / Warranty / AMC
              </h6>
            </Col>
            <Col md={3}>
              <Label className="form-label">Installation Date</Label>
              <Input
                type="date"
                name="installation_date"
                value={form.installation_date}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">Warranty End Date</Label>
              <Input
                type="date"
                name="warranty_end_date"
                value={form.warranty_end_date}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">AMC Start Date</Label>
              <Input
                type="date"
                name="amc_start_date"
                value={form.amc_start_date}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">AMC End Date</Label>
              <Input
                type="date"
                name="amc_end_date"
                value={form.amc_end_date}
                onChange={handleChange}
              />
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button outline color="secondary" onClick={toggle} disabled={saving}>
              Cancel
            </Button>
            <Button color="success" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" /> : 'Save Changes'}
            </Button>
          </div>
        </form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default PurifierEditOffcanvas;
